import { useContext, useRef } from 'react';
import { ThemeContext } from 'styled-components';
import { getPathFromNode } from 'utils/helpers';
import { isCovered } from '@/config/constants';
import useEndDate from 'utils/hooks/useEndDate';
import useExportName from '../useExportName';
import useGetHierarchy from 'utils/hooks/useGetHierarchy';

/** A settled row that carries no volume metric is what "no data" means. */
export const isEmptyRow = ({ rowQuery, row, volumeKey }) =>
    (rowQuery.isSuccess || rowQuery.isError) &&
    !(row && Object.prototype.hasOwnProperty.call(row, volumeKey));

/**
 * What every entity page derives once its source hook has the entity and the
 * row: the export-name closure, the hierarchy, the coverage verdict and the
 * chart's ref and title.
 *
 * `details` is the entity's own fields - its query's, or until that lands
 * whatever stands in for it on the row - so a page can show a name, image
 * or depth before the entity query settles.
 *
 * Everything a source decides for itself - which weeks hook, which row query,
 * what counts as a name, when it is empty - it decides in its own hook and
 * hands here as a value.
 */
const useEntityBase = ({
    dataSource,
    slug,
    pathSlug = slug,
    entityType,
    entityId,
    controller,
    entity,
    entityQuery,
    details,
    country,
    week,
    year,
    weeksQuery,
    row,
    rowQuery,
    series = null,
    name,
    isLoading,
    isEmpty,
    coverage = 'market',
    coveredCountries,
    exportEntity,
    timeSeriesName,
    ...rest
}) => {
    const theme = useContext(ThemeContext);
    const date = useEndDate(slug);
    const timeSeriesRef = useRef();

    const getHierarchy = useGetHierarchy();
    const { parent: parentId, hierarchy_hint: hierarchyHint } =
        getHierarchy(details?.hierarchies) || {};

    const exportName = useExportName({
        country,
        source: slug,
        entity: exportEntity === undefined ? name : exportEntity,
        date,
    });

    /* `hint` and `hintCat1` are not interchangeable: on a hint with no cat1,
       `hint` asks the matrix and fails where `hintCat1` skips the check. */
    const coverageFailed =
        coverage === 'hint'
            ? !!(
                  hierarchyHint &&
                  country &&
                  !isCovered({
                      geo: country?.text_id,
                      dataSource,
                      cat1: hierarchyHint?.cat1,
                  })
              )
            : coverage === 'hintCat1'
              ? !!(
                    country &&
                    hierarchyHint?.cat1 &&
                    !isCovered({
                        geo: country?.text_id,
                        dataSource,
                        cat1: hierarchyHint.cat1,
                    })
                )
              : !!(
                    coveredCountries &&
                    country &&
                    !coveredCountries.includes(country.text_id)
                );

    return {
        dataSource,
        slug,
        entityType,
        entityId,
        controller,
        entity,
        entityQuery,
        details,
        theme,
        country,
        countryIds: country ? [country.id] : null,
        week,
        year,
        weeksQuery,
        date,
        exportName,
        row,
        rowQuery,
        series,
        name,
        timeSeriesRef,
        timeSeriesName:
            timeSeriesName ??
            `${name} (${country ? country.code || country.name : null})`,
        isLoading,
        isEmpty,
        coverageFailed,
        hierarchyHint,
        parentId,
        generateLocation: (node, nodeParentId) =>
            getPathFromNode(node, nodeParentId, pathSlug),
        ...rest,
    };
};

export default useEntityBase;
